import React, { useState } from "react";
import styled from "@emotion/styled";
import LuClock8 from "@assets/icons/luclock8.svg";
import MegaphoneIcon from "@assets/icons/Megaphone.svg";
import InfoSvg from "@assets/icons/info.svg";
import thumbsUpAnimation from "@assets/lottie/thumbs-up.json";
import { Player } from "@lottiefiles/react-lottie-player";
import { LearnItem, MediaType, ArticleModule } from "@domain/learn/learn-service";
import { physioMeTabletBreakpoint } from "@ui/style";
import { ArticleRenderer } from "@ui/components/learn/article-renderer";
import { VideoPlayer } from "@ui/components/common/video-player";
import { TipsModuleSection } from "@ui/components/learn/tips-module-section";
import { AnimatedButton, AnimationType } from "@ui/components/common/animated-button";
import type { BlocksContent } from "@strapi/blocks-react-renderer";
import { colors } from "@ui/style/colors";
import { fonts } from "@ui/style/font";
import { QuoteModuleSection } from "@ui/components/learn/quote-module-section";
import { useMediaDuration } from "@ui/hooks/use-media-duration";
import { useI18n } from "@core/i18n/i18n";
import { analyticsService } from "@core/service-container";
import { AnalyticsCategory } from "@domain/analytics/analytics-types";
import dayjs from "dayjs";

interface DocumentViewProps {
	item: LearnItem;
}

interface MediaSectionProps {
	item: LearnItem;
}

const IMAGE_EXTENSION_REGEX = /\.(png|jpe?g|webp|svg)$/i;
const QUOTE_MODULE_COMPONENT = "embed.quote-module";
const TIPS_MODULE_COMPONENT = "embed.educational-article-tips-module";

const MediaSection = ({ item }: MediaSectionProps): JSX.Element => {
	const hasMedia = !!item.mediaUrl;
	const isVideo = hasMedia && item.mediaType === MediaType.VIDEO;
	const isImage = hasMedia && item.mediaUrl && IMAGE_EXTENSION_REGEX.test(item.mediaUrl);

	return (
		<MediaContainer isVisible={hasMedia}>
			{isVideo && item.mediaUrl && (
				<VideoWrapper>
					<VideoPlayer url={item.mediaUrl} displayVideoGradient={false} autoPlay controls />
				</VideoWrapper>
			)}
			{isImage && item.mediaUrl && <ArticleImage src={item.mediaUrl} alt={item.title} />}
		</MediaContainer>
	);
};

interface ArticleModuleSectionProps {
	module: ArticleModule;
}

const ArticleModuleSection = ({ module }: ArticleModuleSectionProps): JSX.Element | null => {
	switch (module.__component) {
		case QUOTE_MODULE_COMPONENT:
			return <QuoteModuleSection quoteModule={module} />;
		case TIPS_MODULE_COMPONENT:
			return <TipsModuleSection tipsModule={module} />;
		default:
			return null;
	}
};

export const DocumentView = ({ item }: DocumentViewProps): JSX.Element => {
	const [showReferences, setShowReferences] = useState(true);
	const [feedback, setFeedback] = useState<"liked" | "disliked" | null>(null);
	const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(false);
	const [hideFeedbackButtons, setHideFeedbackButtons] = useState(false);
	const [showThankYouMessage, setShowThankYouMessage] = useState(false);
	const playerRefUp = React.useRef<Player>(null);
	const playerRefDown = React.useRef<Player>(null);
	const { durationLabel } = useMediaDuration(item.mediaType, item.mediaUrl, item.duration);
	const hasContent = !!(item.content && Array.isArray(item.content) && item.content.length > 0);
	const hasCallout = !!(item.callout && Array.isArray(item.callout) && item.callout.length > 0);

	const articleModules = (item.articleModules ?? []).sort((a, b) => {
		if (a.__component === QUOTE_MODULE_COMPONENT && b.__component !== QUOTE_MODULE_COMPONENT) return -1;
		if (a.__component !== QUOTE_MODULE_COMPONENT && b.__component === QUOTE_MODULE_COMPONENT) return 1;
		return 0;
	});

	const toggleReferences = () => setShowReferences(prev => !prev);

	const handleFeedback = (type: "liked" | "disliked") => {
		if (isFeedbackSubmitted) return;
		setFeedback(type);
		setIsFeedbackSubmitted(true);
		analyticsService.trackEvent({
			category: AnalyticsCategory.LEARN,
			action: "contentFeedback",
			params: [item.documentId, type],
		});
		if (type === "liked") {
			playerRefUp.current?.play();
		} else {
			playerRefDown.current?.play();
		}
		setTimeout(() => {
			setHideFeedbackButtons(true);
			setShowThankYouMessage(true);
		}, 1000);
	};

	const { format, formatDateFull } = useI18n();

	return (
		<DocumentContainer>
			{/* Header Section */}
			<DocumentHeader>
				<DocumentTitle as="h1">{item.title}</DocumentTitle>

				<MetadataRow role="contentinfo" aria-label="Article metadata">
					<AuthorText isVisible={!!item.author} aria-label={`Author: ${item.author}`}>
						{item.author}
					</AuthorText>
					<PublicationDateText
						isVisible={!!item.publicationDate}
						aria-label={`Published: ${formatDateFull(dayjs(item.publicationDate))}`}
					>
						{formatDateFull(dayjs(item.publicationDate))}
					</PublicationDateText>
					<DurationRow isVisible={!!durationLabel} aria-label={`Duration: ${durationLabel}`}>
						<LuClock8 width={16} height={16} aria-hidden="true" />
						<DurationText>{durationLabel}</DurationText>
					</DurationRow>
				</MetadataRow>
			</DocumentHeader>

			{/* Media Section */}
			<MediaSection item={item} />

			{/* Content Section */}
			<DocumentContent isVisible={hasContent}>
				<ArticleRenderer content={(item.content ?? []) as unknown as BlocksContent} />
			</DocumentContent>

			{/* Article Module Section */}
			{articleModules.map(module => (
				<ArticleModuleSection key={module.id} module={module} />
			))}

			{/* Callout Section */}
			<CalloutBox isVisible={hasCallout} role="note" aria-label="Important information">
				<MegaphoneIconWrapper aria-hidden="true">
					<MegaphoneIcon />
				</MegaphoneIconWrapper>
				<CalloutContent>
					<ArticleRenderer content={(item.callout ?? []) as unknown as BlocksContent} />
				</CalloutContent>
			</CalloutBox>

			{/* References Section */}
			<ReferenceSection isVisible={!!item.reference} role="complementary" aria-labelledby="references-title">
				<ReferenceHeader>
					<ReferenceHeaderLeft>
						<ReferenceTitle id="references-title">{format("learn.References.title")}</ReferenceTitle>
						<InfoIcon aria-hidden="true">
							<InfoSvg />
						</InfoIcon>
					</ReferenceHeaderLeft>
					<ToggleButton
						onClick={toggleReferences}
						aria-expanded={showReferences}
						aria-label={`${showReferences ? "Hide" : "Show"} references`}
						type="button"
					>
						{showReferences ? "Hide" : "Show"}
					</ToggleButton>
				</ReferenceHeader>
				<ReferenceDivider />
				<ReferenceText isVisible={showReferences} aria-live="polite">
					{item.reference}
				</ReferenceText>
			</ReferenceSection>

			{/* Feedback Section */}
			<FeedbackSection role="group" aria-labelledby="feedback-title" isVisible={!hideFeedbackButtons}>
				<FeedbackTitle id="feedback-title">{format("learn.Feedback.title")}</FeedbackTitle>
				<FeedbackButtons>
					<AnimatedButton
						type={AnimationType.Scale}
						onClick={() => handleFeedback("liked")}
						isActive={feedback === "liked"}
						aria-label="Like this content"
						aria-pressed={feedback === "liked"}
						className="feedback-button"
						style={{
							width: "56px",
							height: "56px",
							borderRadius: "50%",
							background: feedback === "liked" ? colors.primary : colors.primary10,
							border: "none",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							cursor: "pointer",
						}}
					>
						<Player
							ref={playerRefUp}
							loop={false}
							controls={false}
							src={thumbsUpAnimation}
								style={{
									height: "24px",
									width: "24px",
									filter:
										feedback === "liked"
											? "brightness(0) invert(1)"
											: "brightness(0) saturate(100%) invert(33%) sepia(89%) saturate(1749%) hue-rotate(201deg) brightness(95%) contrast(95%)",
								}}
							aria-hidden="true"
						/>
					</AnimatedButton>
					<AnimatedButton
						type={AnimationType.Scale}
						onClick={() => handleFeedback("disliked")}
						isActive={feedback === "disliked"}
						aria-label="Dislike this content"
						aria-pressed={feedback === "disliked"}
						className="feedback-button"
						style={{
							width: "56px",
							height: "56px",
							borderRadius: "50%",
							background: feedback === "disliked" ? colors.primary : colors.primary10,
							border: "none",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							cursor: "pointer",
						}}
					>
						<Player
							ref={playerRefDown}
							loop={false}
							controls={false}
							src={thumbsUpAnimation}
								style={{
									height: "24px",
									width: "24px",
									transform: "rotate(180deg)",
									filter:
										feedback === "disliked"
											? "brightness(0) invert(1)"
											: "brightness(0) saturate(100%) invert(33%) sepia(89%) saturate(1749%) hue-rotate(201deg) brightness(95%) contrast(95%)",
								}}
							aria-hidden="true"
						/>
					</AnimatedButton>
				</FeedbackButtons>
			</FeedbackSection>

			<ThankYouMessage isVisible={showThankYouMessage} role="status" aria-live="polite">
				{format("mediaContent.feedback.thanks")}
			</ThankYouMessage>

			{/* Footer Section */}
			<FooterSection isVisible={!!item.footer} role="contentinfo">
				<FooterText>{item.footer}</FooterText>
			</FooterSection>
		</DocumentContainer>
	);
};

const DocumentContainer = styled.div`
	width: 100%;
`;

const DocumentHeader = styled.header`
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 12px;
	margin-bottom: 24px;
`;

const DocumentTitle = styled.h1`
	font-family: ${fonts.header};
	color: ${colors.black};
	margin: 0;

	@media ${physioMeTabletBreakpoint} {
		font-size: 28px;
	}

	@media (max-width: 520px) {
		font-size: 24px;
	}
`;

const MetadataRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
	font-size: 13px;
`;

const AuthorText = styled.span<{ isVisible: boolean }>`
	font-family: ${fonts.body};
	color: ${colors.grey50};
	display: ${({ isVisible }) => (isVisible ? "inline" : "none")};
`;

const PublicationDateText = styled.span<{ isVisible: boolean }>`
	font-family: ${fonts.body};
	color: ${colors.grey50};
	display: ${({ isVisible }) => (isVisible ? "inline" : "none")};

	&::before {
		content: "|";
		margin: 0 8px;
		color: ${colors.grey25};
	}
`;

const DurationRow = styled.div<{ isVisible: boolean }>`
	display: ${({ isVisible }) => (isVisible ? "flex" : "none")};
	align-items: center;
	gap: 4px;
	color: ${colors.grey50};

	&::before {
		content: "|";
		margin: 0 8px;
		color: ${colors.grey25};
	}

	svg {
		fill: ${colors.grey50};
		color: ${colors.grey50};
		width: 14px;
		height: 14px;
	}
`;

const DurationText = styled.span`
	font-family: ${fonts.body};
`;

// Media Styles
const MediaContainer = styled.div<{ isVisible: boolean }>`
	width: 100%;
	margin-bottom: ${({ isVisible }) => (isVisible ? "40px" : "0")};
	max-height: ${({ isVisible }) => (isVisible ? "2000px" : "0")};
	opacity: ${({ isVisible }) => (isVisible ? "1" : "0")};
	overflow: hidden;
	transition:
		max-height 0.3s ease-in-out,
		opacity 0.3s ease-in-out,
		margin-bottom 0.3s ease-in-out;
`;

const ArticleImage = styled.img`
	width: 100%;
	height: auto;
	max-height: 490px;
	object-fit: cover;
	border-radius: 16px;
	display: block;

	@media ${physioMeTabletBreakpoint} {
		height: 373px;
		max-height: 373px;
	}

	@media (max-width: 520px) {
		height: 192px;
		max-height: 192px;
		border-radius: 12px;
	}
`;

const VideoWrapper = styled.div`
	width: 100%;
	border-radius: 16px;
	overflow: hidden;
	display: block;

	@media (max-width: 520px) {
		border-radius: 12px;
	}
`;

// Content Styles
const DocumentContent = styled.article<{ isVisible: boolean }>`
	width: 100%;
	font-family: ${fonts.body};

	color: ${colors.black};
	margin-bottom: ${({ isVisible }) => (isVisible ? "32px" : "0")};
	max-height: ${({ isVisible }) => (isVisible ? "none" : "0")};
	opacity: ${({ isVisible }) => (isVisible ? "1" : "0")};
	overflow: ${({ isVisible }) => (isVisible ? "visible" : "hidden")};
	transition:
		max-height 0.3s ease-in-out,
		opacity 0.3s ease-in-out,
		margin-bottom 0.3s ease-in-out;

	h1,
	h2,
	h3,
	h4,
	h5,
	h6 {
		font-family: ${fonts.header};
		font-size: 24px;
		font-weight: 700;
		color: ${colors.black};
		margin: 32px 0 16px 0;

		&:first-child {
			margin-top: 0;
		}
	}

	p {
		margin: 0 0 16px 0;
		color: ${colors.black};
		font-size: 18px;
		line-height: 1.7;

		&:last-child {
			margin-bottom: 0;
		}
	}

	ul,
	ol {
		margin: 0 0 24px 0;
		padding-left: 28px;
		font-size: 18px;
		color: ${colors.black};
		line-height: 1.7;
	}

	li {
		margin-bottom: 8px;
		font-family: ${fonts.body};
		font-size: 18px;
		font-weight: 400;
		color: ${colors.black};
		&:last-child {
			margin-bottom: 0;
		}
	}

	ul li {
		list-style-type: none;
		position: relative;
		padding-left: 4px;

		&::before {
			content: "";
			position: absolute;
			left: -24px;
			top: 10px;
			width: 6px;
			height: 6px;
			background-color: ${colors.primary};
			border-radius: 50%;
		}
	}

	strong {
		font-family: ${fonts.header};
		font-weight: 700;
		color: ${colors.black};
	}
`;

// Callout Styles
const CalloutBox = styled.div<{ isVisible: boolean }>`
	position: relative;
	background: ${colors.white};
	border-radius: 16px;
	padding: 72px 32px 32px 32px;
	margin-bottom: 32px;
	margin-top: 120px;
	display: ${props => (props.isVisible ? "block" : "none")};
	box-shadow:
		0px 44.344px 12.81px 0px rgba(162, 156, 201, 0),
		0px 28.577px 10.84px 0px rgba(162, 156, 201, 0.03),
		0px 15.767px 9.854px 0px rgba(162, 156, 201, 0.1),
		0px 6.898px 6.898px 0px rgba(162, 156, 201, 0.17),
		0px 1.971px 3.942px 0px rgba(162, 156, 201, 0.2);

	@media ${physioMeTabletBreakpoint} {
		padding: 64px 24px 24px 24px;
	}

	@media (max-width: 520px) {
		padding: 56px 20px 20px 20px;
	}
`;

const MegaphoneIconWrapper = styled.div`
	position: absolute;
	top: -45px;
	left: 32px;
	width: 70px;
	height: 70px;
	align-items: center;
	justify-content: center;

	@media ${physioMeTabletBreakpoint} {
		width: 60px;
		height: 60px;
		top: -40px;
		left: 24px;
	}

	@media (max-width: 520px) {
		width: 50px;
		height: 50px;
		top: -40px;
		left: 20px;
	}
`;

const CalloutContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 24px;

	h1,
	h2,
	h3,
	h4,
	h5,
	h6 {
		font-family: ${fonts.header};
		font-size: 24px;
		font-weight: 700;
		line-height: 1;
		color: ${colors.black};
		margin: 0;
	}

	p {
		font-family: ${fonts.body};
		font-size: 18px;
		font-weight: 400;
		line-height: 1.7;
		color: ${colors.black};
		margin: 0;

		&:not(:last-child) {
			margin-bottom: 16px;
		}
	}

	strong {
		font-family: ${fonts.header};
		font-weight: 700;
	}

	ul,
	ol {
		margin: 0;
		padding-left: 28px;
		font-family: ${fonts.body};
		font-size: 18px;
		font-weight: 400;
		line-height: 1.7;
		color: ${colors.black};
	}

	li {
		margin-bottom: 8px;

		&:last-child {
			margin-bottom: 0;
		}
	}

	ul li {
		list-style-type: none;
		position: relative;
		padding-left: 4px;

		&::before {
			content: "";
			position: absolute;
			left: -24px;
			top: 10px;
			width: 6px;
			height: 6px;
			background-color: ${colors.primary};
			border-radius: 50%;
		}
	}
`;

// Reference Styles
const ReferenceSection = styled.section<{ isVisible: boolean }>`
	margin-top: ${({ isVisible }) => (isVisible ? "40px" : "0")};
	background: rgba(86, 86, 113, 0.05);
	border-radius: 8px;
	padding: ${({ isVisible }) => (isVisible ? "16px" : "0")};
	display: ${({ isVisible }) => (isVisible ? "flex" : "none")};
	flex-direction: column;
	gap: 8px;
	max-height: ${({ isVisible }) => (isVisible ? "2000px" : "0")};
	opacity: ${({ isVisible }) => (isVisible ? "1" : "0")};
	overflow: hidden;
	transition:
		max-height 0.3s ease-in-out,
		opacity 0.3s ease-in-out,
		margin-top 0.3s ease-in-out,
		padding 0.3s ease-in-out;
`;

const ReferenceHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
`;

const ReferenceHeaderLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const ReferenceTitle = styled.h2`
	font-family:
		"Rubik",
		system-ui,
		-apple-system,
		"Segoe UI",
		Roboto,
		"Helvetica Neue",
		Arial;
	font-size: 20px;
	font-weight: 500;
	color: ${colors.black};
	margin: 0;
`;

const InfoIcon = styled.div`
	width: 16px;
	height: 16px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	color: ${colors.grey};

	svg {
		width: 16px;
		height: 16px;
	}
`;

const ToggleButton = styled.button`
	font-family: ${fonts.body};
	font-size: 16px;
	font-weight: 700;
	color: ${colors.grey};
	background: none;
	border: none;
	cursor: pointer;
	padding: 0;
	margin: 0;
	transition: opacity 0.2s ease;

	&:hover {
		opacity: 0.7;
	}
`;

const ReferenceDivider = styled.div`
	width: 100%;
	height: 1px;
	background: ${colors.grey25};
	flex-shrink: 0;
`;

const ReferenceText = styled.div<{ isVisible: boolean }>`
	font-family:
		"Nunito",
		system-ui,
		-apple-system,
		"Segoe UI",
		Roboto,
		"Helvetica Neue",
		Arial;
	font-size: 14px;
	line-height: 1.6;
	color: ${colors.black};
	white-space: pre-wrap;
	padding-left: 16px;
	max-height: ${({ isVisible }) => (isVisible ? "2000px" : "0")};
	opacity: ${({ isVisible }) => (isVisible ? "1" : "0")};
	overflow: hidden;
	transition:
		max-height 0.3s ease-in-out,
		opacity 0.3s ease-in-out;

	ul {
		margin: 0;
		padding: 0;
		list-style: none;
	}

	li {
		margin-left: 21px;
		position: relative;

		&::before {
			content: "•";
			position: absolute;
			left: -21px;
		}
	}

	p {
		margin: 4px 0;

		&:first-child {
			margin-top: 0;
		}

		&:last-child {
			margin-bottom: 0;
		}
	}
`;

// Feedback Styles
const FeedbackSection = styled.section<{ isVisible: boolean }>`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
	margin-top: ${({ isVisible }) => (isVisible ? "40px" : "0")};
	width: 100%;
	max-height: ${({ isVisible }) => (isVisible ? "2000px" : "0")};
	opacity: ${({ isVisible }) => (isVisible ? "1" : "0")};
	overflow: hidden;
	transition:
		max-height 0.3s ease-in-out,
		opacity 0.3s ease-in-out,
		margin-top 0.3s ease-in-out;
`;

const ThankYouMessage = styled.div<{ isVisible: boolean }>`
    width: 100%;
    text-align: center;
    font-family:
        "Rubik",
        system-ui,
        -apple-system,
        "Segoe UI",
        Roboto,
        "Helvetica Neue",
        Arial;
    font-size: 16px;
    font-weight: 500;
    color: ${colors.black};
    margin-top: 40px;
    max-height: ${({ isVisible }) => (isVisible ? "200px" : "0")};
    opacity: ${({ isVisible }) => (isVisible ? "1" : "0")};
    overflow: hidden;
    transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out, margin-top 0.3s ease-in-out;
`;

const FeedbackTitle = styled.p`
	font-family:
		"Rubik",
		system-ui,
		-apple-system,
		"Segoe UI",
		Roboto,
		"Helvetica Neue",
		Arial;
	font-size: 16px;
	font-weight: 500;
	color: ${colors.black};
	margin: 0;
	text-align: center;
`;

const FeedbackButtons = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 16px;
`;

// Footer Styles
const FooterSection = styled.footer<{ isVisible: boolean }>`
	width: 100%;
	max-width: 400px;
	margin: ${({ isVisible }) => (isVisible ? "32px auto 0" : "0 auto")};
	padding-top: ${({ isVisible }) => (isVisible ? "24px" : "0")};
	max-height: ${({ isVisible }) => (isVisible ? "500px" : "0")};
	opacity: ${({ isVisible }) => (isVisible ? "1" : "0")};
	overflow: hidden;
	transition:
		max-height 0.3s ease-in-out,
		opacity 0.3s ease-in-out,
		margin-top 0.3s ease-in-out,
		padding-top 0.3s ease-in-out;
`;

const FooterText = styled.div`
	font-family:
		"Nunito",
		system-ui,
		-apple-system,
		"Segoe UI",
		Roboto,
		"Helvetica Neue",
		Arial;
	font-size: 13px;
	line-height: 1.6;
	color: ${colors.black};
	text-align: center;
`;
